/**
 * Sync Store
 *
 * ISSUE_068: Local-First 아키텍처 전환을 위한 sync 설정 스토어.
 *
 * 게이트 분화 (ISSUE_177 BM):
 *  - syncEnabled = **세션(수업 일정) 다기기 동기화** 게이트 (= 프리미엄 자격: active + past_due 7일 유예,
 *    isPremiumActive 단일 소스). 세션 엔진(sessionsService·SyncInitializer 워커)만 소비. 마스터데이터
 *    (학생·수업·성적·문제 등)는 이 게이트와 무관하게 무료에서도 항상 서버 저장·백업됨(RLS 경계).
 *    미결제(trialing)/미로드/만료(canceled) = 세션 동기화 OFF (fail-closed).
 *  - ISSUE_182: scratch(연습장) 폐기 — 학원 단일. 학원 미선택(active.id==='')이면 구독 없어 자동 OFF.
 *
 * workspace / subscription 변경 시 자동으로 syncEnabled가 업데이트됨.
 *
 * @module core/store/useSyncStore
 */

import { create } from "zustand";
import { useWorkspaceStore } from "../useWorkspaceStore";
import { useSubscriptionStore, isPremiumActive } from "../useSubscriptionStore";

// ============================================================================
// TYPES
// ============================================================================

interface SyncState {
  /** 서버 동기화 활성화 여부 — **세션 다기기 동기화** 게이트(ISSUE_177 프리미엄). 세션 엔진(sessionsService·SyncInitializer)이 소비. */
  syncEnabled: boolean;
  /**
   * ISSUE_177 엔타이틀먼트: 구독 플랜. **마스터데이터 realtime(라이브 멀티에디터)** 프리미엄 게이트의 근거.
   * 현재 결제 active 로 파생 — 좌석 빌링(`seats>1`→paid) 도입 시 그쪽으로 전환.
   */
  plan: "free" | "paid";
  /** ISSUE_177 엔타이틀먼트: 좌석(교사) 수. 무료=1, 유료=N. 구독 페이지 전까지 기본 1(읽기측 인터페이스만). */
  seats: number;
}

interface SyncActions {
  /** workspace + subscription 상태에 따라 syncEnabled·엔타이틀먼트 업데이트 */
  updateFromWorkspace: () => void;
}

type SyncStore = SyncState & SyncActions;

// ============================================================================
// SYNC 결정 로직
// ============================================================================

function computeSyncEnabled(): boolean {
  // 세션 다기기 동기화 게이트 = 프리미엄 자격(active + past_due 7일 유예). 미로드/미결제(trialing)/만료(canceled) = OFF (fail-closed).
  // 마스터데이터는 이 게이트 무관(무료에서도 서버 저장). 자격 구독 로드 시 subscribe 로 즉시 ON.
  // ISSUE_182: scratch 폐기 — 학원 단일. 학원 미선택(active.id==='')이면 구독 없어 자동 OFF.
  const { academySubscription } = useSubscriptionStore.getState();
  return isPremiumActive(academySubscription);
}

/**
 * ISSUE_177 엔타이틀먼트 파생. `plan` = 마스터데이터 realtime(라이브 멀티에디터) 프리미엄 게이트.
 * 현재 `plan` 은 결제 active 로 파생 — 좌석 빌링(`seats>1`→paid) 도입 시 그쪽으로 전환.
 * `seats` 는 인터페이스만(기본 1); 구독 페이지 구현 후 실제 좌석 수 주입. (seats 로 plan 파생 금지 — 스텁이라 paid 도 1)
 */
function computeEntitlement(): { plan: "free" | "paid"; seats: number } {
  const { academySubscription } = useSubscriptionStore.getState();
  const paid = isPremiumActive(academySubscription);
  return { plan: paid ? "paid" : "free", seats: 1 };
}

// ============================================================================
// STORE
// ============================================================================

export const useSyncStore = create<SyncStore>()((set) => ({
  syncEnabled: computeSyncEnabled(),
  ...computeEntitlement(),

  updateFromWorkspace: () => {
    set({ syncEnabled: computeSyncEnabled(), ...computeEntitlement() });
  },
}));

// ============================================================================
// AUTO-SUBSCRIBE: workspace 변경 시 자동 반영
// ============================================================================

let _prevWorkspaceId = useWorkspaceStore.getState().active.id;

useWorkspaceStore.subscribe((state) => {
  const currentId = state.active.id;
  if (currentId !== _prevWorkspaceId) {
    _prevWorkspaceId = currentId;
    useSyncStore.getState().updateFromWorkspace();
  }
});

// subscription 변경 시에도 반영
useSubscriptionStore.subscribe(() => {
  useSyncStore.getState().updateFromWorkspace();
});

// ============================================================================
// NON-REACT GETTER (스토어 밖에서도 접근 가능)
// ============================================================================

/**
 * 현재 sync 활성화 여부를 반환.
 * React 바깥 (서비스 레이어, 유틸리티)에서 사용.
 */
export function getSyncEnabled(): boolean {
  return useSyncStore.getState().syncEnabled;
}

/**
 * ISSUE_177: **마스터데이터 realtime(라이브 멀티에디터)** 프리미엄 여부. React 바깥용.
 * NB: 세션 동기화는 `getSyncEnabled()`(별도 게이트). 현재 두 값은 결제 active 로 일치하나 의미가 다름(향후 분기 가능).
 */
export function getLiveSyncEnabled(): boolean {
  return useSyncStore.getState().plan === "paid";
}

/** ISSUE_177: 현재 구독 플랜('free'|'paid'). React 바깥용. */
export function getPlan(): "free" | "paid" {
  return useSyncStore.getState().plan;
}

// ============================================================================
// SELECTOR HOOKS
// ============================================================================

export const useSyncEnabled = () =>
  useSyncStore((state) => state.syncEnabled);

/** ISSUE_177: 마스터데이터 realtime(라이브 멀티에디터) 프리미엄 여부 (React). */
export const useLiveSyncEnabled = () =>
  useSyncStore((state) => state.plan === "paid");

/** ISSUE_177: 현재 구독 플랜. */
export const usePlan = () => useSyncStore((state) => state.plan);

/** ISSUE_177: 좌석(교사) 수. */
export const useSeats = () => useSyncStore((state) => state.seats);
